import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircleQuestion, Send, X, Sparkles, ArrowUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRole } from "@/contexts/RoleContext";
import {
  answerQuestion,
  suggestedEntries,
  allEntries,
  isRouteContextual,
  type AssistantContext,
  type KbEntry,
} from "@/lib/helpAssistant";

/** Auth-flow routes where the assistant must not render (spec AC 12). */
const EXCLUDED_PREFIXES = [
  "/login", "/register", "/confirm-signup", "/forgot-password",
  "/auth/callback", "/callback", "/select-role", "/switch-role",
];

interface ChatMessage {
  from: "user" | "assistant";
  text?: string;
  entry?: KbEntry | null;
  related?: KbEntry[];
  fallback?: boolean;
}

/** Minimal markdown-lite: renders **bold** segments only. */
function RichText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function AssistantBubble({ msg, onAsk, onNavigate }: {
  msg: ChatMessage;
  onAsk: (entry: KbEntry) => void;
  onNavigate: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-muted/60 ring-1 ring-border/50 shadow-sm px-3.5 py-2.5 text-sm text-foreground/90 leading-relaxed animate-in fade-in slide-in-from-bottom-1 duration-200">
        {msg.fallback ? (
          <>
            I don't have an answer for that yet. For account-specific issues, please{" "}
            <Link to="/contact" onClick={onNavigate} className="underline font-medium">contact support</Link>
            {msg.related && msg.related.length > 0 && " — or did you mean one of these?"}
          </>
        ) : msg.entry ? (
          <RichText text={msg.entry.answer} />
        ) : (
          <span>{msg.text}</span>
        )}
        {msg.entry && msg.entry.links.length > 0 && (
          <span className="mt-2 flex flex-wrap gap-2">
            {msg.entry.links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={onNavigate}
                className="group/link inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/15 rounded-lg px-2 py-1 transition-colors"
              >
                {link.label} <ArrowUpRight className="w-3 h-3 group-hover/link:translate-x-px group-hover/link:-translate-y-px transition-transform" />
              </Link>
            ))}
          </span>
        )}
      </div>
      {msg.related && msg.related.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {msg.related.map(rel => (
            <button
              key={rel.id}
              type="button"
              onClick={() => onAsk(rel)}
              className="group/chip inline-flex items-center gap-1 text-xs font-medium pl-3 pr-2.5 py-1.5 rounded-full border border-primary/20 bg-gradient-to-b from-primary/[0.04] to-primary/10 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] hover:from-primary/10 hover:to-primary/15 hover:border-primary/40 hover:shadow-sm hover:-translate-y-px active:translate-y-0 transition-all duration-150 text-left"
            >
              {rel.question}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function HelpAssistant() {
  const location = useLocation();
  const { role } = useRole();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [browseAll, setBrowseAll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ctx: AssistantContext = useMemo(() => ({
    route: location.pathname,
    role: role === "student" || role === "teacher" ? role : null,
  }), [location.pathname, role]);

  const suggestions = useMemo(() => {
    const asked = new Set(messages.filter(m => m.entry).map(m => m.entry!.id));
    return suggestedEntries(ctx, 4 + asked.size).filter(s => !asked.has(s.id)).slice(0, 4);
  }, [ctx, messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const excluded = EXCLUDED_PREFIXES.some(p => location.pathname.startsWith(p));
  if (excluded) return null;

  const askEntry = (entry: KbEntry) => {
    setBrowseAll(false);
    setMessages(m => [
      ...m,
      { from: "user", text: entry.question },
      { from: "assistant", entry, related: [] },
    ]);
  };

  const handleSend = () => {
    const q = input.trim();
    if (!q) return;
    setInput("");
    const reply = answerQuestion(q, ctx);
    setMessages(m => [
      ...m,
      { from: "user", text: q },
      reply.entry
        ? { from: "assistant", entry: reply.entry, related: reply.related }
        : { from: "assistant", fallback: true, related: reply.related },
    ]);
  };

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          type="button"
          aria-label="Open help assistant"
          data-tour="help"
          onClick={() => setOpen(true)}
          className="group fixed bottom-24 right-4 sm:bottom-5 sm:right-5 z-[55] p-3.5 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-lg shadow-primary/40 ring-1 ring-white/20 hover:scale-110 hover:shadow-xl transition-all duration-200"
        >
          <MessageCircleQuestion className="w-6 h-6 group-hover:rotate-6 transition-transform" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className={cn(
            "fixed z-[55] flex flex-col overflow-hidden bg-card border border-border/70 shadow-2xl",
            "animate-in fade-in slide-in-from-bottom-4 duration-300",
            // Mobile: bottom sheet. Desktop: floating card bottom-right.
            "inset-x-0 bottom-0 rounded-t-2xl max-h-[75svh]",
            "sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[380px] sm:rounded-2xl sm:max-h-[560px]"
          )}
          role="dialog"
          aria-label="Help assistant"
        >
          {/* Header */}
          <div className="relative flex items-center gap-2.5 px-4 py-3 bg-gradient-to-r from-primary via-primary to-primary/85 text-primary-foreground shrink-0 overflow-hidden">
            <div className="absolute -top-8 right-8 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none" />
            <span className="relative w-8 h-8 rounded-full bg-white/15 ring-1 ring-white/25 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight">Help Assistant</p>
              <p className="text-xs text-primary-foreground/70 leading-tight">
                Answers from our help guide — not a live agent
              </p>
            </div>
            <button
              type="button"
              aria-label="Close help assistant"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-white/15 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px]">
            {/* Greeting + suggestions */}
            <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-muted/60 ring-1 ring-border/50 shadow-sm px-3.5 py-2.5 text-sm text-foreground/90 leading-relaxed animate-in fade-in slide-in-from-bottom-1 duration-200">
              Hi! Ask me anything about using BookMyCoaching{role ? "" : " — or how to get started"}.
              For account-specific issues, use{" "}
              <Link to="/contact" onClick={() => setOpen(false)} className="underline font-medium">contact support</Link>.
            </div>
            {messages.map((msg, i) =>
              msg.from === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-gradient-to-br from-primary to-primary/85 text-primary-foreground shadow-sm px-3.5 py-2.5 text-sm leading-relaxed animate-in fade-in slide-in-from-bottom-1 duration-200">
                    {msg.text}
                  </div>
                </div>
              ) : (
                <AssistantBubble key={i} msg={msg} onAsk={askEntry} onNavigate={() => setOpen(false)} />
              )
            )}

            {/* Route-aware suggestions — recomputed on navigation so the chips
                always match the page the user is on, even mid-conversation. */}
            {suggestions.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                  {suggestions.some(s => isRouteContextual(s, ctx.route))
                    ? "Questions about this page"
                    : "Popular questions"}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(browseAll ? allEntries(ctx) : suggestions).map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => askEntry(s)}
                      className="group/chip inline-flex items-center gap-1 text-xs font-medium pl-3 pr-2.5 py-1.5 rounded-full border border-primary/20 bg-gradient-to-b from-primary/[0.04] to-primary/10 text-primary shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] hover:from-primary/10 hover:to-primary/15 hover:border-primary/40 hover:shadow-sm hover:-translate-y-px active:translate-y-0 transition-all duration-150 text-left"
                    >
                      {s.question}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setBrowseAll(b => !b)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary px-2 py-1 -mx-2 rounded-lg hover:bg-primary/5 transition-colors"
                >
                  {browseAll ? <>Show less <ChevronUp className="w-3 h-3" /></> : <>Browse all questions <ChevronDown className="w-3 h-3" /></>}
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border/60 bg-background shrink-0">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSend(); if (e.key === "Escape") setOpen(false); }}
              placeholder="Type your question…"
              className="flex-1 h-10 px-3 rounded-xl bg-muted/50 border border-border/60 text-sm outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition-all"
            />
            <button
              type="button"
              aria-label="Send question"
              onClick={handleSend}
              disabled={!input.trim()}
              className="group/send w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-md shadow-primary/25 flex items-center justify-center disabled:opacity-40 disabled:shadow-none enabled:hover:shadow-lg enabled:hover:shadow-primary/30 enabled:hover:-translate-y-px enabled:active:translate-y-0 enabled:active:scale-95 transition-all duration-150 shrink-0"
            >
              <Send className="w-4 h-4 group-hover/send:translate-x-0.5 group-hover/send:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
